import React, { useEffect, useState } from 'react';
import { useTheme } from '../../../theme-context';
import { COLORS_LIST } from '../../../utils/constants/insights-colors';
import { DOCUMENT_TABS } from '../../../utils/constants/documents-tabs';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import {
    tryToFetchAllStaffUsageData,
    tryToFetchQuestionsDocuments,
    tryToFetchQuestionsDocumentsStats,
    tryToFetchSentimentQuestions,
    tryToFetchSentimentQuestionsStats,
    tryToFetchSourceConfusions,
    tryToFetchSourceConfusionsStats,
    tryToFetchStaffUsageStats,
} from '../../../store/brokers/partners/documents/documentsSlice';
import UnansweredQuestionsModal from './modals/UnansweredQuestionsModal';
import SentimentBarChart from './charts/SentimentChart';
import BarChart from './charts/BarChart';
import PieChart from './charts/PieChart';
import DocumentsModal from './modals/DocumentsModal';
import TeamTable from '../../../components/teams/TeamTable';
import KpiBox from './components/KpiBox';
import TableActions from '../../../shared/table/table-actions';
import Button from '../../../shared/button';
import { tryToFetchUnansweredQuestionStats } from '../../../store/brokers/partners/unanswered-questions/unansweredQuestionsSlice';

// const kpiList = [
//     { title: 'KPI', percentage: '-17.3', total: '700000' },
//     { title: 'KPI', percentage: '10.2', total: '1234500' },
//     { title: 'KPI', percentage: '-3.4', total: '1234' },
// ]

const Homepage = () => {
    const { theme } = useTheme();
    const dispatch = useAppDispatch();
    const [showUnanswerModal, setShowUnanswerModal] = useState<boolean>(false);
    const [showDocumentsModal, setShowDocumentsModal] =
        useState<boolean>(false);
    const [loadingDownload, setLoadingDownload] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<any>(DOCUMENT_TABS?.[0]);
    const [barData, setBarData] = useState<any>();
    const [unansweredData, setUnansweredData] = useState<any>();
    const [usageData, setUsageData] = useState<any>();
    const [statsData, setStatsData] = useState<any>();
    const [xAxis, setXAxis] = useState();
    const documents = useAppSelector((state) => state?.documents);

    const onGettingDocumentsData = async () => {
        try {
            const formatData = {
                pageSize: 10,
                pageNumber: 1,
            };
            if (activeTab?.value === 'sources') {
                await dispatch(tryToFetchQuestionsDocuments(formatData)).unwrap();
                const res = await dispatch(tryToFetchQuestionsDocumentsStats()).unwrap();
                setBarData((prevBarData: any) => ({
                    ...(prevBarData || {}),
                    [activeTab?.value || '']:
                        res?.length > 0
                            ? res?.map((item: any) => item?.count)
                            : [],
                }));

                setXAxis((prevBarData: any) => ({
                    ...(prevBarData || {}),
                    [activeTab?.value || '']:
                        res?.length > 0
                            ? res?.map((item: any) => item?.source)
                            : [],
                }));
            }
            if (activeTab?.value === 'confusion') {
                await dispatch(tryToFetchSourceConfusions(formatData)).unwrap();
                const res = await dispatch(
                    tryToFetchSourceConfusionsStats(),
                ).unwrap();
                setBarData((prevBarData: any) => ({
                    ...(prevBarData || {}),
                    [activeTab?.value || '']:
                        res?.length > 0
                            ? res?.map((item: any) => item?.confusionTrigger)
                            : [],
                }));

                setXAxis((prevBarData: any) => ({
                    ...(prevBarData || {}),
                    [activeTab?.value || '']:
                        res?.length > 0
                            ? res?.map((item: any) => item?.source)
                            : [],
                }));
            }
            if (activeTab?.value === 'sentiment') {
                await dispatch(
                    tryToFetchSentimentQuestions(formatData),
                ).unwrap();
                const res = await dispatch(
                    tryToFetchSentimentQuestionsStats(),
                ).unwrap();
                setBarData((prevBarData: any) => ({
                    ...(prevBarData || {}),
                    [activeTab?.value || '']: res,
                }));
                setXAxis((prevBarData: any) => ({
                    ...(prevBarData || {}),
                    [activeTab?.value || '']:
                        res?.length > 0
                            ? res?.map((item: any) => item?.source)
                            : [],
                }));
            }
        } catch (err) {
            console.error(err);
        }
    };

    const onGettingStaffUsageData = async () => {
        try {
            const response = await dispatch(
                tryToFetchStaffUsageStats(),
            ).unwrap();
            setUsageData(response);
        } catch (err) {
            console.error(err);
        }
    };

    const onGettingUnansweredData = async () => {
        try {
            const response = await dispatch(
                tryToFetchUnansweredQuestionStats(),
            ).unwrap();
            setUnansweredData(response);
        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        let formatData: any = statsData;
        if (documents?.questions) {
            formatData = {
                ...(formatData || {}),
                sources: documents?.questions?.data?.elements,
            };
        }
        if (documents?.confusion) {
            formatData = {
                ...(formatData || {}),
                confusion: documents?.confusion?.data?.elements,
            };
        }
        if (documents?.sentiments) {
            formatData = {
                ...(formatData || {}),
                sentiment: documents?.sentiments?.data?.elements,
            };
        }
        setStatsData(formatData);
    }, [documents]);

    useEffect(() => {
        if (!statsData?.[activeTab?.value] || !barData?.[activeTab?.value]) {
            onGettingDocumentsData();
        }
    }, [activeTab]);

    useEffect(() => {
        onGettingDocumentsData();
        onGettingStaffUsageData();
        onGettingUnansweredData();
    }, []);

    const OnClickUnanswerModal = () => {
        setShowUnanswerModal(!showUnanswerModal);
    };

    const onChangeActiveTab = (tab: any) => {
        setActiveTab(tab);
    };

    const onClickViewDocuments = () => {
        setShowDocumentsModal(!showDocumentsModal);
    };

    const onDownloadStaffData = async () => {
        setLoadingDownload(true);
        try {
            const response: any = await dispatch(
                tryToFetchAllStaffUsageData(undefined),
            ).unwrap();
            window.open(response?.excelLink, '_blank');
        } catch (err) {
            setLoadingDownload(false);
        }
        setLoadingDownload(false);
    };

    const maxAnswer = activeTab?.value === 'sources'
        ? Math.max(...((statsData?.[activeTab?.value]?.map((item: any) => Number(item?.count) || 0)) ?? [0]))
        : Math.max(...((statsData?.[activeTab?.value]?.map((item: any) => Number(item?.confusionTrigger) || 0)) ?? [0]));

    return (
        <div>
            <div className="bg-white dark:bg-bgColor-darkMode">
                <p className="text-[#1C1C1C]  dark:text-primary-dark mb-5 text-lg font-semibold">
                    Insights
                </p>
                <div className="flex flex-row justify-between mb-2">
                    <p className="dark:text-primary-dark">
                        Unaswered Questions this month
                    </p>
                    <TableActions>
                        <div
                            onClick={OnClickUnanswerModal}
                            className="bg-white dark:bg-black shadow px-2 py-2 cursor-pointer hover:opacity-80">
                            <p className="dark:text-primary-dark">
                                See all unanswered questions
                            </p>
                        </div>
                    </TableActions>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {unansweredData &&
                        unansweredData?.length > 0 &&
                        unansweredData?.map((item: any, idx: number) => (
                            <div key={idx}>
                                <KpiBox
                                    color={COLORS_LIST?.[idx]}
                                    title={item?.status || ''}
                                    percentage={item?.percentChange || ''}
                                    total={item?.current || ''}
                                    status={item.status}
                                    showUnanswered={OnClickUnanswerModal}
                                />
                            </div>
                        ))}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-8 gap-4 my-4">
                    <div className="col-span-1 lg:col-span-5 !rounded-lg border p-3">
                        <div className="flex flex-col lg:flex-row my-1 lg:items-center">
                            {DOCUMENT_TABS?.map((item: any, idx: number) => {
                                return (
                                    <div
                                        key={idx}
                                        onClick={() => onChangeActiveTab(item)}
                                        className={`cursor-pointer hover:opacity-80 ${activeTab?.value === item?.value
                                            ? 'text-[#1C1C1C] dark:text-primary-dark'
                                            : 'text-[#a4a4a4]'
                                            } mr-5 font-semibold`}>
                                        <p>{item?.label}</p>
                                    </div>
                                );
                            })}
                        </div>
                        {activeTab?.value !== 'sentiment' ? (
                            <BarChart
                                xAxisData={xAxis?.[activeTab?.value] || []}
                                chartData={barData?.[activeTab?.value] || []}
                            />
                        ) : (
                            <SentimentBarChart
                                data={barData?.[activeTab?.value]}
                                xAxisData={xAxis?.[activeTab?.value] || []}
                            />
                        )}
                    </div>
                    <div className="col-span-1 lg:col-span-3 !rounded-lg border p-3">
                        <p className="text-[#1C1C1C] dark:text-primary-dark font-semibold mb-5">
                            {activeTab?.label}
                        </p>
                        {statsData?.[activeTab?.value] &&
                            activeTab?.value !== 'sentiment' &&
                            statsData?.[activeTab?.value]?.map(
                                (item: any, idx: number) => (
                                    <div
                                        key={idx}
                                        className="flex items-center space-x-2 my-2">
                                        <div
                                            title={item.source || item?.document}
                                            className="w-24 text-sm truncate whitespace-nowrap overflow-hidden dark:text-primary-dark">
                                            {item.source || item?.document}
                                        </div>
                                        <div className="flex-1 bg-gray-200 dark:bg-gray-700 h-2 rounded-full relative">
                                            <div
                                                className={`h-2 rounded-full absolute top-0 left-0 ${(item?.count ||
                                                    item?.confusionTrigger) ===
                                                    maxAnswer
                                                    ? 'bg-black dark:bg-[#f59e0c]'
                                                    : 'bg-gray-300 dark:bg-[#f59e0c] dark:bg-opacity-50'
                                                    }`}
                                                style={{ width: `${((item?.count || item?.confusionTrigger) / maxAnswer) * 100}%` }}
                                            />
                                        </div>
                                    </div>
                                ),
                            )}
                        {activeTab?.value === 'sentiment' &&
                            statsData?.[activeTab?.value] &&
                            statsData?.[activeTab?.value]?.map(
                                (item: any, idx: number) => {
                                    const positive = item?.positive || 0;
                                    const neutral = item?.neutral || 0;
                                    const negative = item?.negative || 0;
                                    const total = positive + neutral + negative;

                                    const getWidth = (val: number) =>
                                        total > 0
                                            ? `${(val / total) * 100}%`
                                            : '0%';

                                    return (
                                        <div
                                            key={idx}
                                            className="flex items-center space-x-2 my-2">
                                            <div
                                                title={item?.document}
                                                className="w-24 text-sm truncate whitespace-nowrap overflow-hidden dark:text-primary-dark">
                                                {item?.document}
                                            </div>
                                            <div className="flex-1 h-2 bg-gray-200 rounded-full relative overflow-hidden">
                                                <div
                                                    className="h-full bg-[#10B981] hover:[#10B98180] absolute top-0 left-0"
                                                    style={{
                                                        width: getWidth(
                                                            positive,
                                                        ),
                                                    }}
                                                    title={`Positive: ${positive}`}
                                                />
                                                <div
                                                    className="h-full bg-[#F59E0B] hover:bg-[#F59E0B80] absolute top-0"
                                                    style={{
                                                        width: getWidth(
                                                            neutral,
                                                        ),
                                                        left: getWidth(
                                                            positive,
                                                        ),
                                                    }}
                                                    title={`Neutral: ${neutral}`}
                                                />
                                                <div
                                                    className="h-full bg-[#EF4444] hover:bg-[#EF444480] absolute top-0"
                                                    style={{
                                                        width: getWidth(
                                                            negative,
                                                        ),
                                                        left: `calc(${getWidth(
                                                            positive,
                                                        )} + ${getWidth(
                                                            neutral,
                                                        )})`,
                                                    }}
                                                    title={`Negative: ${negative}`}
                                                />
                                            </div>
                                        </div>
                                    );
                                },
                            )}
                        <div className="text-center my-2">
                            <Button
                                label={activeTab?.linkTitle}
                                className="btn-primary-text-underline dark:text-white"
                                onClickButton={onClickViewDocuments}
                            />
                        </div>
                    </div>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-8 gap-4 my-4">
                    <div className="col-span-1 lg:col-span-4 !rounded-lg border p-3">
                        <div className="flex flex-row justify-between items-start">
                            <p className="text-[#1C1C1C] dark:text-primary-dark font-semibold mb-5">
                                Staff Usage
                            </p>
                            <div
                                className={`hover:opacity-80 cursor-pointer ${loadingDownload ? 'opacity-80' : ''
                                    }`}
                                onClick={() =>
                                    !loadingDownload && onDownloadStaffData()
                                }>
                                <img
                                    src={
                                        theme === 'dark'
                                            ? '/assets/insights/downloads-white.svg'
                                            : '/assets/insights/downloads.svg'
                                    }
                                    className={'object-contain w-[20px] mr-2'}
                                />
                            </div>
                        </div>
                        <BarChart
                            showRotate={true}
                            xAxisData={
                                usageData?.length > 0
                                    ? usageData?.map(
                                        (item: any) => item?.userType,
                                    )
                                    : []
                            }
                            chartData={
                                usageData?.length > 0
                                    ? usageData?.map((item: any) => item?.total)
                                    : []
                            }
                        />
                    </div>
                    <div className="col-span-1 lg:col-span-4 !rounded-lg border p-3">
                        <p className="text-[#1C1C1C] dark:text-primary-dark font-semibold mb-5">
                            Staff Usage
                        </p>
                        <PieChart
                            data={
                                usageData?.length > 0
                                    ? usageData?.map((item: any) => ({
                                        name: item?.userType,
                                        value: item?.total,
                                    }))
                                    : []
                            }
                        />
                    </div>
                </div>
                <div className="my-8">
                    <p className="text-[#1C1C1C] dark:text-primary-dark mb-5 text-lg font-semibold">
                        Team Members
                    </p>
                    <TeamTable hideSelect={true} />
                </div>
                {showUnanswerModal && (
                    <UnansweredQuestionsModal
                        openModal={showUnanswerModal}
                        handleCloseModal={OnClickUnanswerModal}
                    />
                )}
                {showDocumentsModal && (
                    <DocumentsModal
                        openModal={showDocumentsModal}
                        tabOpened={activeTab}
                        handleCloseModal={onClickViewDocuments}
                    />
                )}
            </div>
        </div>
    );
};
export default Homepage;
